import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  signal,
  ViewChild,
} from '@angular/core';

import { StateService, Transition } from '@uirouter/core';
import { LiveAnnouncerService } from 'src/app/accessibility';
import { type FolderItem } from 'src/app/api/services/folder';
import { type Presentation } from 'src/app/api/services/presentation';
import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import {
  ListOperations,
  ListSearchStateService,
  PaginatedScrollingListService,
  ScrollingListStateService,
} from 'src/app/components/scrolling-list';
import { CompanyIdModalComponent } from 'src/app/components/scrolling-list/company-id-modal/company-id-modal.component';
import { PageWithListComponent } from 'src/app/components/scrolling-list/page-with-list.component';
import { type ListItem } from 'src/app/components/scrolling-list/services';
import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import { UsernamePipe } from 'src/app/shared/pipes/username.pipe';
import { FolderService } from 'src/app/shared/services/folder.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { PRESENTATION_FOLDER_OPERATION_SERVICE } from '../../providers/presentation-folder-operation.provider';
import { EditorService } from '../../services/editor.service';

@Component({
  selector: 'presentation-list',
  templateUrl: './presentation-list.component.html',
  styleUrls: ['./presentation-list.component.scss'],
  standalone: false,
  providers: [presentationListProviders()],
})
export class PresentationListComponent
  extends PageWithListComponent<Presentation>
  implements AfterViewInit
{
  private destroyRef = inject(DestroyRef);

  folderId: string | null | undefined;
  folders: FolderItem[] = [];

  tableFields: {
    label: string;
    name: string;
    reverse: boolean;
    renderIf: boolean;
  }[];

  listOperations = {
    name: 'Presentation',
    operations: [
      {
        name: 'Copy to Sub-company',
        beforeBatchAction: (selected) => {
          const plural = selected.length > 1 ? 's' : '';
          return this.modalService.showDialog(CompanyIdModalComponent, {
            title: 'Copy Selected Presentation' + plural,
            description:
              'A copy of ' +
              (plural ? 'each of ' : '') +
              'the selected presentation' +
              plural +
              ' will be added to the Company ID specified below.',
          });
        },
        actionCall: (listItem?, companyId?) => {
          return this.editorService
            .copyPresentationToCompany(listItem.item, companyId)
            .then(() => {
              if (listItem.item.companyId === companyId) {
                this.listService.reload();
              }
            });
        },
        hidden: (selected) => {
          return this.hasFolderSelected(selected);
        },
        requireRole: 'cp',
        mainOperation: false,
      },
      {
        name: 'Make a Copy',
        actionCall: (listItem?, companyId?) => {
          return this.editorService
            .copyPresentationToCompany(
              listItem.item,
              this.companyStateService.getSelectedCompanyId(),
            )
            .then(() => {
              this.listService.reload();
            });
        },
        hidden: (selected) => {
          return this.hasFolderSelected(selected);
        },
        requireRole: 'cp',
        mainOperation: true,
      },
      {
        name: 'Rename',
        beforeBatchAction: (selected) =>
          this.editorService.actionRename(selected.map((item) => item.item)),
        showIn: ['single'],
        icon: 'pencil-line',
        reloadList: true,
      },
      {
        name: 'Move',
        beforeBatchAction: (selectedItems) => {
          return this.editorService.beforeActionMoveFolder(
            selectedItems.map((item) => item.item),
            this.folderId,
          );
        },
        actionCall: (listItem, parentId) => {
          return this.editorService.actionMoveFolder(listItem.item, parentId);
        },
        showIn: ['single', 'bulk'],
        icon: 'move-to-folder',
        mainOperation: true,
        reloadList: true,
      },
      {
        name: 'Export',
        beforeBatchAction: (selected) => {
          return this.modalService.confirm(
            'Export presentations?',
            'An export file will be prepared and emailed to you at <b>' +
              this.userStateService.getUserEmail() +
              '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
            'Export',
            'Cancel',
          );
        },
        actionCall: this.presentationApiService.export.bind(
          this.presentationApiService,
        ),
        groupBy: true,
        requireRole: 'cp',
        mainOperation: true,
      },
      {
        name: 'Delete',
        actionCall: async (listItem?) => {
          await this.presentationFolderOperationService.executeSingleOperation(
            (presentation) =>
              this.editorService.deletePresentationByObject(presentation, true),
            listItem.item,
          );
          if (listItem && listItem.isFolder) {
            await this.folderService.deleteFolder(listItem.item.id);
          }
          return Promise.resolve();
        },
        showActionError: (err?) => {
          if (err && err.status === 409) {
            return true;
          }
          return false;
        },
        requireRole: 'cp ap',
        mainOperation: false,
        icon: 'trash',
        showIn: ['single', 'bulk'],
        isDelete: true,
      },
    ],
  } as ListOperations<Presentation>;

  isHtmlPresentation = this.presentationUtils.isHtmlPresentation;

  showAiButton = signal(
    this.aiCreditsService.canUserUseAI() &&
      this.companyStateService.isAiEnabled(),
  );

  filterConfig = {
    placeholder: 'Search Presentations',
    id: 'presentationSearchInput',
    borderless: true,
  };

  assignedToUsers = '';
  presentationFolderOperationService = inject(
    PRESENTATION_FOLDER_OPERATION_SERVICE,
  );

  @ViewChild('presentationListContainer') presentationListContainer: ElementRef;

  constructor(
    protected userStateService: UserStateService,
    private presentationApiService: PresentationApiService,
    public editorService: EditorService,
    public templateEditorFactory: TemplateEditorService,
    private presentationUtils: PresentationUtilsService,
    private modalService: ModalService,
    public companyStateService: CompanyStateService,
    private liveAnnouncerService: LiveAnnouncerService,
    private usernamePipe: UsernamePipe,
    private transition: Transition,
    protected folderService: FolderService,
    private ngZone: NgZone,
    private stateService: StateService,
    private aiCreditsService: AiCreditsService,
  ) {
    super();

    this.tableFields = [
      { label: 'Name', name: 'name', reverse: false, renderIf: true },
      {
        label: 'Status',
        name: 'revisionStatusName',
        reverse: false,
        renderIf: true,
      },
      {
        label: 'Schedules',
        name: 'scheduleIds',
        reverse: false,
        renderIf: true,
      },
      {
        label: 'Last Modified',
        name: 'changeDate',
        reverse: false,
        renderIf: true,
      },
      {
        label: 'Assigned To',
        name: 'assignedUsersValue',
        reverse: false,
        renderIf:
          this.companyStateService.isK12Customer() ||
          this.companyStateService.isHigherEdCustomer(),
      },
    ];

    this.folderId = this.transition.params().folderId;
    if (this.folderId) {
      this.searchState.setFolderId(this.folderId);
    }
    this.listService.load();
    this.updateMoveAfterAction();
    this.listService.setOperations(this.listOperations);
  }

  ngAfterViewInit(): void {
    const { sortBy, reverse } = this.searchState.getSearchObject();
    const field = this.tableFields.find((f) => f.name === sortBy);
    if (field) {
      this.announceSort(field.label, reverse ? 'descending' : 'ascending');
    }

    this.initDraggableService(
      '.presentation-list-container, .folder-path',
      '.presentation-list-container',
    );
  }

  doSearch = () => {
    this.searchState.doSearch();
  };

  popoverText(assignedUsers: string[]) {
    if (assignedUsers.length > 1) {
      this.assignedToUsers = assignedUsers
        .slice(1)
        .map((user) => (user ? this.usernamePipe.transform(user) : ''))
        .join('<br>');
    } else {
      this.assignedToUsers = '';
    }

    setTimeout(() => {
      this.ngZone.run(() => {});
    });
  }

  getDestroyRef(): DestroyRef {
    return this.destroyRef;
  }

  onHeaderResize(event: { width: number; height: number }) {
    if (this.presentationListContainer) {
      this.presentationListContainer.nativeElement.style.setProperty(
        '--presentation-list-header-height',
        event.height + 'px',
      );

      this.checkViewportSize();
    }
  }

  onFolderClicked(folderId: string) {
    this.transition.router.stateService.go('apps.editor.list', { folderId });
  }

  navigateToCreatePresentation(folderId?: string) {
    this.stateService.go('apps.editor.create', { folderId: folderId || null });
  }

  sort(field: { name: string; label: string; reverse: boolean }) {
    // Get current sort state before changing it
    const currentSort = this.searchState.getSearchObject();
    const wasAlreadySorted = currentSort.sortBy === field.name;
    const willBeReversed = wasAlreadySorted
      ? !currentSort.reverse
      : field.reverse;

    // Perform the sort
    this.searchState.sortBy(field.name, willBeReversed);

    // Announce the sort change
    this.announceSort(field.label, willBeReversed ? 'descending' : 'ascending');
  }

  announceSort(fieldLabel: string, direction: 'ascending' | 'descending') {
    this.liveAnnouncerService.announce(
      `Table sorted by ${fieldLabel}, ${direction} order`,
    );
  }

  hasFolderSelected(selected: ListItem<Presentation>[]) {
    if (!selected) return false;
    return selected.some((item) => item.isFolder && item.selected);
  }
}

function presentationListProviders() {
  return [
    { provide: 'WithListService', useExisting: PresentationApiService },
    { provide: 'ListKind', useValue: 'Presentation' },
    { provide: ListSearchStateService },
    { provide: ScrollingListStateService },
    { provide: PaginatedScrollingListService },
  ];
}
